import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';

export interface StyledSelectOption {
  value: string;
  label: string;
  icon?: string;
  subtitle?: string;
}

interface StyledSelectProps {
  options: StyledSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  required?: boolean;
  name?: string;
}

export default function StyledSelect({
  options,
  value,
  onChange,
  placeholder = 'Selecione...',
  disabled = false,
  className = '',
  error = false,
  required = false,
  name
}: StyledSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Encontrar opção selecionada
  const selectedOption = useMemo(
    () => options.find(opt => opt.value === value),
    [options, value]
  );

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll automático para opção destacada
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        // Quando abrir, destacar a opção selecionada
        const selectedIndex = options.findIndex(opt => opt.value === value);
        setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : -1);
      }
    }
  };

  const handleSelect = (option: StyledSelectOption) => {
    onChange(option.value);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          const selectedIndex = options.findIndex(opt => opt.value === value);
          setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
        } else {
          setHighlightedIndex(prev => 
            prev < options.length - 1 ? prev + 1 : prev
          );
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
        }
        break;

      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && options[highlightedIndex]) {
          handleSelect(options[highlightedIndex]);
        } else if (!isOpen) {
          setIsOpen(true);
          const selectedIndex = options.findIndex(opt => opt.value === value);
          setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;

      case 'Tab':
        setIsOpen(false);
        break;
    }
  };

  const baseClasses = `
    relative w-full rounded-lg border px-4 py-2.5 text-left shadow-sm
    transition-colors duration-200 cursor-pointer
    ${disabled ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'bg-white hover:border-gray-400'}
    ${error ? 'border-red-300 focus-within:border-red-500 focus-within:ring-red-500' : 'border-gray-300 focus-within:border-brand-primary-500 focus-within:ring-brand-primary-500'}
    ${isOpen ? 'ring-2 ring-opacity-50 border-brand-primary-500 ring-brand-primary-500' : ''}
  `;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Select trigger */}
      <div
        className={baseClasses}
        onClick={handleToggle}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {selectedOption?.icon && (
              <span className="text-base flex-shrink-0">{selectedOption.icon}</span>
            )}
            <div className="flex flex-col flex-1 min-w-0">
              <span className={`text-sm truncate ${selectedOption ? 'text-gray-900' : 'text-gray-500'}`}>
                {selectedOption?.label || placeholder}
              </span>
              {selectedOption?.subtitle && (
                <span className="text-xs text-gray-500 truncate">
                  {selectedOption.subtitle}
                </span>
              )}
            </div>
          </div>

          <ChevronDown 
            className={`w-5 h-5 text-gray-700 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Dropdown list */}
      {isOpen && (
        <ul
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto"
          role="listbox"
        >
          {options.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500 text-center">
              Nenhuma opção disponível
            </li>
          ) : (
            options.map((option, index) => (
              <li
                key={option.value}
                className={`
                  px-4 py-2.5 cursor-pointer transition-colors
                  ${index === highlightedIndex ? 'bg-brand-primary-50' : ''}
                  ${option.value === value ? 'bg-brand-primary-100 font-medium' : 'hover:bg-gray-50'}
                `}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                role="option"
                aria-selected={option.value === value}
              >
                <div className="flex items-center gap-2">
                  {option.icon && (
                    <span className="text-base flex-shrink-0">{option.icon}</span>
                  )}
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-sm text-gray-900">{option.label}</span>
                    {option.subtitle && (
                      <span className="text-xs text-gray-500">{option.subtitle}</span>
                    )}
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      )}

      {/* Hidden input for form compatibility */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={value}
          required={required}
        />
      )}
    </div>
  );
}